import React, { useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Link, Typography } from '@mui/material';
import { Trans, useTranslation } from 'react-i18next';
import ModalCard from '../ui/ModalCard';
import TextInput from '../ui/TextInput';
import SocialAuthButtons from './SocialAuthButtons';
import { ApiError, requestRegistration } from '../../api/authApi';
import type { InitializeRegisterErrorCode } from '../../api/authApi';

type RegisterErrorCode = InitializeRegisterErrorCode | 'UNKNOWN';

const ERROR_KEY: Record<RegisterErrorCode, string> = {
  INVALID_EMAIL: 'register.email.errors.invalidEmail',
  EMAIL_ALREADY_IN_USE: 'register.email.errors.emailAlreadyInUse',
  UNKNOWN: 'register.email.errors.generic',
};

interface RegisterEmailFormCardProps {
  onSent: (email: string) => void;
}

export default function RegisterEmailFormCard({ onSent }: RegisterEmailFormCardProps) {
  const { t } = useTranslation();

  const [email, setEmail] = useState('');
  // Consent is opt-in: unchecked by default even though the design shows it ticked.
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorCode, setErrorCode] = useState<RegisterErrorCode | null>(null);

  const canSubmit = email.trim() !== '' && termsAccepted && !submitting;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setErrorCode(null);
    try {
      await requestRegistration(email);
      onSent(email);
    } catch (err) {
      setErrorCode(err instanceof ApiError ? (err.code as RegisterErrorCode) : 'UNKNOWN');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSocialSignup = (provider: 'google' | 'apple') => {
    // TODO(backend): kick off the OAuth sign-up flow for the given provider.
    // eslint-disable-next-line no-console
    console.info('[mock] social signup', provider);
  };

  return (
    <ModalCard title={t('register.cardTitle')} onSubmit={handleSubmit}>
      <Typography variant="h2" component="h1">
        {t('register.email.headline')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <TextInput
          id="register-email"
          type="email"
          autoComplete="email"
          label={t('register.email.emailLabel')}
          placeholder={t('register.email.emailPlaceholder')}
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setErrorCode(null);
          }}
        />
        {errorCode && (
          <Typography variant="caption" sx={(theme) => ({ color: theme.app.destructive })}>
            {t(ERROR_KEY[errorCode])}
          </Typography>
        )}
      </Box>

      <FormControlLabel
        sx={{ alignItems: 'flex-start', m: 0, gap: 1.5 }}
        control={
          <Checkbox
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            sx={{ p: 0, color: 'text.secondary' }}
          />
        }
        label={
          <Typography variant="body2" sx={(theme) => ({ color: theme.app.foreground })}>
            <Trans
              i18nKey="register.email.terms"
              components={{
                terms: <Link href="#" underline="always" color="inherit" />,
                privacy: <Link href="#" underline="always" color="inherit" />,
              }}
            />
          </Typography>
        }
      />

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={!canSubmit}
        sx={(theme) => ({
          backgroundColor: theme.app.primary,
          color: theme.app.primaryForeground,
          '&:hover': { backgroundColor: theme.app.primaryHover },
        })}
      >
        {t('register.email.submit')}
      </Button>

      <SocialAuthButtons onSelect={handleSocialSignup} />
    </ModalCard>
  );
}
